/**
 * TEMPORARY in-app feedback tool — context capture.
 *
 * Self-test logger so Edd can run through the app and jot "what's not right" as he
 * goes, each note auto-tagged with WHERE and WHAT he was using. This whole
 * `src/feedback` folder is a bolt-on: to strip it, delete the folder, remove the
 * one <FeedbackButton/> import in Shell, and drop the feedback plugin line from
 * vite.config.ts. (The menuLocation field in the shell store is general UI state
 * and can stay or go.)
 *
 * `summariseContext` and `makeEntry` are pure (unit-tested); `captureContext`
 * reads the live stores.
 */
import { useShellStore } from '@/store/shell';
import { usePositionSourceStore } from '@/store/positionSource';
import { isTrainingSetup } from '@/chart-sources';
import { useChartPlotterStore } from '@/store';
import { useEnvStore } from '@/store/environment';
import { useSatelliteStore } from '@/store/satellite';
import { useRangeRingsStore } from '@/store/rangeRings';
import { useAisStore } from '@/store/ais';
import { APP_VERSION, APP_COMMIT } from '@/update/appVersion';
import { useChartSourcesStore } from '@/store/chartSources';
import { useRoutingStore } from '@/store/routing';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { useMeasureStore } from '@/store/measure';
import { useSettingsStore } from '@/store/settings';
import { residencyTraceSnapshot } from '@/map/residencyTrace';
import { lastRunTail, lastRunVerdict, freezeLine } from '@/diag/HeartbeatRunner';
import { rendererDeathSummary } from '@/diag/rendererDeaths';
import { canvasHealthSummary } from '@/map/canvasHealth';
import { gestureDiagLine } from '@/map/MapView';
import { nativeAnchorLine } from '@/safety/anchorNative';
import { useChartPacksStore } from '@/store/chartPacks';
import { hasPrivatePackHost } from '@/chart-sources/packsConfig';
import { getChartAccessToken } from '@/chart-sources/chartAccess';
import { lastCaptureDiag } from './captureScreenshot';

export interface FeedbackOverlays {
  satellite: boolean;
  tidalStreams: boolean;
  ais: boolean;
  catzoc: boolean;
  rangeRings: boolean;
  weather: boolean;
}

export interface FeedbackContext {
  at: string;
  /**
   * Which build this report came from, e.g. "2026.08.05.291 (291) 7cefdb9".
   *
   * FIRST FIELD ON PURPOSE, because it is the first question every report
   * raises and the one that has cost the most time. A report saying a bug is
   * still there is worthless without it: on 5 Aug three reports came in against
   * an APK that predated the fixes, because the CI run carrying them had failed
   * at the test step and never published one, and there was no way to tell from
   * the reports themselves. There is a whole earlier report that says nothing
   * but "Is this one from a later build?" (fb-mseejv5h-0).
   *
   * The Diagnostics panel has shown this for a while. The bug reports, which are
   * the things that actually get read later, did not.
   */
  build: string;
  shellMode: string;
  worldMode: string;
  /** Which sheet is open (e.g. 'menu', 'layers', 'route'), or null at rest. */
  openSheet: string | null;
  /** The menu drill location (e.g. "Layers & Chart → Chart provider"), or null. */
  menuLocation: string | null;
  /** The active chart tool id (pan/measure/vrm/ebl/dr/ep/...). */
  activeTool: string;
  /** A one-line summary of what's being done right now. */
  activity: string;
  /** When an object-detail sheet is open, what object it is; else null. */
  selectedObject: string | null;
  overlays: FeedbackOverlays;
  chartProvider: string;
  symbolSet: string;
  palette: string;
  centre: [number, number] | null;
  zoomApprox: number | null;
  ownship: [number, number] | null;
  /**
   * The last residency plans (which chart cells were kept and dropped, and why),
   * plus any cell-load or source failures. This is the readout that exists
   * nowhere on screen, and both residency field bugs so far were diagnosable
   * only from it. Optional, so the pure summariser stays pure for its tests;
   * captureContext fills it from the live buffer.
   */
  residency?: string[];
  /**
   * How the run BEFORE this one ended, and its last few pulses.
   *
   * An anchor watch left overnight came back frozen, and every scrap of evidence
   * about why had gone by the morning. This is the answer to that: the app
   * writes itself a pulse every few seconds, and carries the last run's into the
   * next report. If it says the pulses were arriving later and later before they
   * stopped, the app was drowning and the heap figure says whether it was
   * memory. If it says they were on time and then simply stopped, something took
   * the thread away and we should be looking somewhere else entirely.
   */
  lastRun?: string;
  lastRunTicks?: string[];
  /**
   * Stretches where the page was frozen in the background and nothing in
   * JavaScript ran at all. Absent when there have been none. Its own field
   * because a run can end cleanly having been frozen for twenty minutes in the
   * middle, and no alarm in the app is checked while it is frozen.
   */
  frozen?: string;
  /**
   * Times Android has taken the WebView's renderer process, and whether it
   * crashed or was simply reclaimed to free memory. Absent when it never has,
   * which is the ordinary case. A backgrounded chart app is a fat target for a
   * phone that wants memory back, and a renderer taken from under us is one
   * honest reading of an app that came back showing nothing but black.
   */
  rendererDeaths?: string;
  /**
   * Times the chart canvas was not the size of the screen, and times the WebGL
   * context was lost. Absent when neither has happened. A chart filling half the
   * phone with black under it and a chart that simply drew nothing look the same
   * in a photograph, and this is what tells them apart.
   */
  canvas?: string;
  /** Whether the map's gesture handlers were enabled when the report was filed:
   *  a map that will not pan with healthy pulses is a handler question first. */
  gestures?: string;
  /**
   * The native anchor watch's own pulse. Read next to the JavaScript pulses it
   * says which of two very different things happened: pulses stopping while this
   * carried on is a frozen page or a dead renderer, both stopping together is the
   * whole app being killed. From inside the web app those look identical, and
   * they want opposite fixes.
   */
  anchorNative?: string;
  /**
   * How the screenshot's chart layer was read (path + blackness sample), so a
   * black or chart-less shot names its own cause in the report. See
   * lastCaptureDiag in captureScreenshot.ts.
   */
  capture?: string;
  /**
   * The RASTER chart state: which provider, how many charts it resolved to, and
   * each pack's status. The residency trace covers the vector cells only, so
   * when Antares drew nothing at Tobermory the report said nothing at all about
   * it and the cause had to be read out of the source. A chart that will not
   * draw should say why in the report, same as everything else here.
   */
  charts?: string;
  /**
   * The boat figures that drive the scaled hull and the bow offset. The first
   * report of the hull not appearing could not be answered from the report,
   * because nothing here said which of the three fields was actually set.
   */
  boat?: string;
}

export interface FeedbackEntry {
  id: string;
  at: string;
  area: string;
  text: string;
  /** Relative path of the saved screenshot (set by the endpoint), if captured. */
  screenshot?: string;
  context: FeedbackContext;
}

/** The raw store values summariseContext turns into a saved context. */
export interface RawContext {
  now: number;
  shellMode: string;
  worldMode: string;
  activeSheet: string | null;
  menuLocation: string | null;
  activeTool: string;
  selectedObject: string | null;
  measurePointCount: number;
  buildingRoute: boolean;
  following: 'route' | 'goto' | null;
  fixArmed: boolean;
  anchorPickArmed: boolean;
  transitActive: boolean;
  headingActive: boolean;
  anchorSet: boolean;
  scrubberOpen: boolean;
  satelliteOn: boolean;
  overlayMode: 'streams' | 'weather';
  tidalStreamGroupOn: boolean;
  aisOn: boolean;
  catzocOn: boolean;
  rangeRingsOn: boolean;
  chartProvider: string;
  symbolSet: string;
  palette: string;
  viewBounds: [number, number, number, number] | null;
  ownship: [number, number] | null;
}

/**
 * Approximate web-mercator zoom from a view's longitude span. Deliberately rough
 * (ignores viewport width and latitude) — enough to remember roughly how zoomed
 * in we were when the note was made.
 */
export function approxZoomFromBounds(b: [number, number, number, number] | null): number | null {
  if (!b) return null;
  const lonSpan = Math.abs(b[2] - b[0]);
  if (!(lonSpan > 0)) return null;
  return Math.round(Math.log2(360 / lonSpan) * 10) / 10;
}

function centreOf(b: [number, number, number, number] | null): [number, number] | null {
  if (!b) return null;
  return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
}

/** Pure: the most salient "what is the user doing" summary from the raw flags. */
export function deriveActivity(r: RawContext): string {
  if (r.activeTool === 'measure') {
    return `Measuring (${r.measurePointCount} point${r.measurePointCount === 1 ? '' : 's'})`;
  }
  if (r.activeTool && r.activeTool !== 'pan' && r.activeTool !== 'none') {
    return `Tool: ${r.activeTool}`;
  }
  if (r.fixArmed) return 'Fix builder: waiting for a mark';
  if (r.anchorPickArmed) return 'Anchor: waiting for a tap to place it';
  if (r.transitActive) return 'Placing a transit';
  if (r.headingActive) return 'Placing a heading / back bearing';
  if (r.buildingRoute) return 'Building a route';
  if (r.following === 'route') return 'Following a route';
  if (r.following === 'goto') return 'Going to a waypoint';
  if (r.anchorSet) return 'Anchor watch set';
  if (r.scrubberOpen) return 'Tidal-stream scrubber open';
  if (r.selectedObject) return `Object detail: ${r.selectedObject}`;
  if (r.menuLocation) return `Menu: ${r.menuLocation}`;
  return 'Viewing the chart';
}

/** Pure: turn raw store values into the saved context (unit-tested). */
export function summariseContext(r: RawContext): FeedbackContext {
  return {
    at: new Date(r.now).toISOString(),
    build: buildLine(),
    shellMode: r.shellMode,
    worldMode: r.worldMode,
    openSheet: r.activeSheet,
    menuLocation: r.menuLocation,
    activeTool: r.activeTool,
    activity: deriveActivity(r),
    selectedObject: r.selectedObject,
    overlays: {
      satellite: r.satelliteOn,
      tidalStreams: r.tidalStreamGroupOn && r.overlayMode === 'streams',
      ais: r.aisOn,
      catzoc: r.catzocOn,
      rangeRings: r.rangeRingsOn,
      weather: r.overlayMode === 'weather',
    },
    chartProvider: r.chartProvider,
    symbolSet: r.symbolSet,
    palette: r.palette,
    centre: centreOf(r.viewBounds),
    zoomApprox: approxZoomFromBounds(r.viewBounds),
    ownship: r.ownship,
  };
}

let seq = 0;
/** Pure: build a feedback entry from text + area + a captured context. */
export function makeEntry(text: string, area: string, context: FeedbackContext): FeedbackEntry {
  const id = `fb-${Date.now().toString(36)}-${(seq++).toString(36)}`;
  return { id, at: context.at, area: area.trim(), text: text.trim(), context };
}

/** A short label for the charted object behind an open detail sheet, if any. */
function selectedObjectLabel(): string | null {
  const shell = useShellStore.getState();
  if (shell.activeSheet === 'feature') {
    const f = useChartPlotterStore.getState().selectedFeature;
    if (f) {
      const name = f.properties.attrs.name as string | undefined;
      return name || f.properties.objClass || 'charted object';
    }
  }
  if (shell.activeSheet === 'diamond') return 'Tidal diamond';
  if (shell.activeSheet === 'ais') return 'AIS target';
  return null;
}

/** Gather the live store state into a context block (called when the modal opens). */
/**
 * One line describing the RASTER chart state: the provider's resolved chart
 * count, where those charts came from, and each pack's status. Never includes
 * the access token itself, only whether one is set.
 */
function chartsLine(): string {
  try {
    const cs = useChartSourcesStore.getState();
    const packs = useChartPacksStore.getState();
    const statuses = Object.entries(packs.runtime)
      .filter(([, r]) => r.status !== 'idle')
      .map(([id, r]) => `${id}:${r.status}${r.error ? '!' : ''}`);
    const gate = hasPrivatePackHost()
      ? `gate=${getChartAccessToken() ? 'token' : 'NO-TOKEN'}`
      : 'gate=off';
    return (
      `bundled=${cs.bsbCharts.length} pack=${cs.packCharts.length} ` +
      `cellSrc=${cs.packCellSources.length} listed=${packs.packs.length} ` +
      `list=${packs.manifestStatus} ${gate}` +
      (statuses.length ? ` | ${statuses.join(' ')}` : ' | no packs on')
    );
  } catch {
    return 'unavailable';
  }
}

/** The boat figures behind the scaled hull and the bow offset. */
/**
 * The build, in one line a human can read off a report.
 *
 * Version NAME and CODE and COMMIT, because each answers a different question:
 * the name is when, the code is which CI run, and only the commit says what code
 * is actually in it.
 */
function buildLine(): string {
  return `${APP_VERSION.versionName} (${APP_VERSION.versionCode}) ${APP_COMMIT}`;
}

function boatLine(): string {
  try {
    const s = useSettingsStore.getState();
    const n = (v: number | null | undefined) => (v == null ? 'unset' : String(v));
    return `len=${n(s.lengthM)} beam=${n(s.beamM)} fromBow=${n(s.gnssFromBowM)} fromCentre=${n(s.gnssFromCentreM)} draught=${n(s.draughtM)} icon=${s.ownshipIcon}`;
  } catch {
    return 'unavailable';
  }
}

export function captureContext(): FeedbackContext {
  const base = captureContextBase();
  const d = lastCaptureDiag();
  return {
    ...base,
    charts: chartsLine(),
    boat: boatLine(),
    residency: residencyTraceSnapshot(),
    lastRun: lastRunVerdict().summary,
    frozen: freezeLine() ?? undefined,
    lastRunTicks: lastRunTail(),
    rendererDeaths: rendererDeathSummary() ?? undefined,
    canvas: canvasHealthSummary() ?? undefined,
    gestures: gestureDiagLine() ?? undefined,
    anchorNative: nativeAnchorLine() ?? undefined,
    capture: d
      ? `map=${d.hadMap} canvas=${d.canvasW}x${d.canvasH} path=${d.path} nonBlack=${d.nonBlackSampled}/${d.sampled}` +
        (d.overlaySampled != null ? ` overlayOpaque=${d.overlayOpaque}/${d.overlaySampled}` : '')
      : undefined,
  };
}

function captureContextBase(): FeedbackContext {
  const chart = useChartPlotterStore.getState();
  const shell = useShellStore.getState();
  const env = useEnvStore.getState();
  const routing = useRoutingStore.getState();
  return summariseContext({
    now: Date.now(),
    shellMode: shell.mode,
    worldMode: isTrainingSetup(chart.chartProviderId, usePositionSourceStore.getState().sourceKind)
      ? 'training'
      : 'real',
    activeSheet: shell.activeSheet,
    menuLocation: shell.menuLocation,
    activeTool: chart.activeTool,
    selectedObject: selectedObjectLabel(),
    measurePointCount: useMeasureStore.getState().points.length,
    buildingRoute: routing.buildingRouteId != null,
    following: routing.followRouteId != null ? 'route' : routing.goto != null ? 'goto' : null,
    fixArmed: shell.fixArmed,
    anchorPickArmed: shell.anchorPickArmed,
    transitActive: shell.transitArmed || shell.pendingTransit != null,
    headingActive: shell.headingArmed != null,
    anchorSet: useAnchorWatchStore.getState().anchor != null,
    scrubberOpen: env.scrubberOpen,
    satelliteOn: useSatelliteStore.getState().enabled,
    overlayMode: env.overlayMode,
    tidalStreamGroupOn: !!chart.groupVisibility['tidal-stream'],
    aisOn: useAisStore.getState().enabled,
    catzocOn: !!chart.groupVisibility['catzoc'],
    rangeRingsOn: useRangeRingsStore.getState().enabled,
    chartProvider: chart.chartProviderId,
    symbolSet: chart.symbolSet,
    palette: chart.palette,
    viewBounds: useChartSourcesStore.getState().viewBounds,
    ownship: chart.ownship.position,
  });
}
